import { useState } from "react";
import Axios from "utils/Axios";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";
import CoverLayout from "./CoverLayout";
import bgImage from "assets/images/city-profile.jpg";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = "Your email address is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const data = JSON.stringify({
      action: "request_reset",
      email: email,
    });

    setLoading(true);
    setApiError("");

    try {
      let res = await Axios.post("/?type=forgot_password", data, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.status === 200) {
        setSuccess(true);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        "An unexpected error occurred. Please try again.";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CoverLayout image={bgImage}>
      <Card>
        <MKBox
          variant="gradient"
          bgColor="dark"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MKTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Forgot Password
          </MKTypography>
          <MKTypography display="block" variant="button" color="white" my={1}>
            Enter your email to reset your password
          </MKTypography>
        </MKBox>
        <MKBox p={3}>
          {apiError && (
            <div
              className="bg-red-50 border border-red-400 text-red-500 px-4 py-2 mx-0 rounded-lg text-sm relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{apiError}</span>
            </div>
          )}

          {success ? (
            <MKBox textAlign="center" py={2}>
              <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
              <MKTypography variant="h5" mt={2}>
                Reset Link Sent
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={2}>
                We've sent password reset instructions to your email address.
                Please check your inbox and follow the instructions.
              </MKTypography>
              <MKTypography variant="body2" color="text">
                If you don't receive an email within a few minutes, please check your spam folder.
              </MKTypography>
              <MKBox mt={4}>
                <MKButton
                  component={Link}
                  to="/sign-in/"
                  variant="gradient"
                  color="dark"
                >
                  Return to Login
                </MKButton>
              </MKBox>
            </MKBox>
          ) : (
            <MKBox component="form" role="form" onSubmit={handleSubmit}>
              <MKBox mb={2}>
                <MKInput
                  type="email"
                  variant="standard"
                  label="Email"
                  fullWidth
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors((prevErrors) => ({ ...prevErrors, email: "" }));
                  }}
                  error={!!errors.email}
                  helperText={errors.email}
                  FormHelperTextProps={{
                    sx: { color: "red" },
                  }}
                />
              </MKBox>
              <MKBox mt={3} mb={1}>
                <MKButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Send Reset Link"
                  )}
                </MKButton>
              </MKBox>
              <MKBox mt={3} mb={1} textAlign="center">
                <MKTypography variant="button" color="text">
                  Remember your password?{" "}
                  <MKTypography
                    component={Link}
                    to="/login/"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Sign In
                  </MKTypography>
                </MKTypography>
              </MKBox>
            </MKBox>
          )}
        </MKBox>
      </Card>
    </CoverLayout>
  );
}

export default ForgotPassword;
